<script setup>
import {reactive, onMounted, onUnmounted} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'CertificationsComponent';

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  show: [],
  certificationTypes: [],
  addExpiration: false,
  status: false,
  openCertificationsForm: false,
  loading: false,
  loaded: true,
  isEditing: false,
});

const formatDate = (date) => {
  if (!date) return null

  return new Date(date).toLocaleDateString();
}

const parseDateWithoutTZ = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const toggleActive = (index) => {
  if (data.show.includes(index)) {
    data.show = data.show.filter((entry) => entry !== index)
    return
  }
  data.show.push(index)
}

const getAllCertifications = () => {
  data.loading = true;
  ApiWrapper.post('certifications', 'get-certifications', {
        personId: props.contextIdA,
      })
      .then((response) => {
        data.certificationTypes = response.certifications;
        data.status = false;
        for (let item in data.certificationTypes) {
          if (data.certificationTypes[item].value === true) {
            data.status = true;
          }
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.openCertificationsForm = false;
        data.loading = false;
      });
}

const submit = () => {
  data.loading = true;
  ApiWrapper.post('certifications', 'create-certifications', {
        certifications: data.certificationTypes,
        personId: props.contextIdA,
      })
      .then((response) => {
        getAllCertifications();
        ToastUtil.successMsg('Created certification');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
        data.openCertificationsForm = false;
      })
}
const update = (item) => {
  data.loading = true;
  ApiWrapper.post('certifications', 'update-certifications', {
        certificationTypes: item,
        personId: props.contextIdA
      })
      .then((response) => {
        getAllCertifications();
        ToastUtil.successMsg('Updated certification');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
        data.openCertificationsForm = false;
      })
}

onMounted(()=>{

  getAllCertifications();

  activeComponentStore.registerComponent(
      compName,
      [
        'disbursements',
        'certificationsList'
      ],
      getAllCertifications
  );
})

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});
</script>

<template>
  <v-container fluid v-if="data.loaded===true">
    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>Certifications</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-spacer></v-spacer>

        <v-btn
            size="small"
            v-if="!data.openCertificationsForm && data.status === false && Permissions.checkPermissions(['certifications.create'])"
            class="primary"
            :loading="data.loading"
            @click="data.openCertificationsForm = true"
        >
          Add
        </v-btn>
        <v-btn
            size="small"
            v-if="!data.openCertificationsForm && data.status === true && Permissions.checkPermissions(['certifications.update'])"
            class="primary"
            @click="data.openCertificationsForm = true"
        >
          Edit
        </v-btn>
      </v-toolbar>

      <v-row>
        <v-col class="py-0">
          <!-- List of Current Certifications -->
          <v-row class="">
            <v-col class="">
              <v-list-subheader
                  v-if="!data.openCertificationsForm && data.status === false"
              >No Certifications - Click 'Add'</v-list-subheader
              >
              <v-row v-if="!data.openCertificationsForm && data.status === true" no-gutters class="ml-2">
                <v-col cols="6">
                  <v-list-subheader>Type</v-list-subheader>
                </v-col>
                <v-col cols="6">
                  <v-list-subheader>Expiration</v-list-subheader>
                </v-col>
              </v-row>

              <v-list
                  single-line
                  class=""
                  v-if="!data.openCertificationsForm"
              >
                <template v-for="(item, index) in data.certificationTypes" :key="item">

                  <v-list-item v-if="item.value === true" :key="item.id" :class="[new Date() > new Date(item.expiration) && item.expiration !== null ? 'red lighten-4' : '']">
                      <v-list-item-title
                      >{{item.label}}</v-list-item-title
                      >



                      <v-tooltip left v-if="typeof item.expiration == 'string' && item.expiration">
                        <template v-slot:activator="{ on, props }">
                          <v-list-item-title
                              v-if="new Date() < new Date(item.expiration)"
                              class="text-decoration-underline primary--text"
                              @click="toggleActive(index)"
                              style="cursor:pointer"
                              v-bind="props"
                              v-on="on"
                          >
                            {{formatDate(item.expiration)}}
                          </v-list-item-title>
                          <v-list-item-title
                              v-else
                              @click="toggleActive(index)"
                              v-bind="props"
                              v-on="on"
                          >
                            Expired: {{formatDate(item.expiration)}}
                          </v-list-item-title>

                        </template>
                        <span>Click to update</span>
                      </v-tooltip>



                      <v-list-item-title
                          v-if="item.expiration === null || item.expiration === 'null' && Permissions.checkPermissions(['certifications.add-expiration'])"
                          class="text-decoration-underline primary--text"
                          style="cursor:pointer"
                          @click="toggleActive(index)"
                      >
                        Add Expiration
                      </v-list-item-title
                      >

                  </v-list-item>

                  <div v-if="data.show.includes(index) && Permissions.checkPermissions(['certifications.update-expiration'])" class="text-center pb-2">
                    <v-date-picker
                        :show-current="true"
                        no-title
                        elevation="1"
                        v-model="item.expiration"
                        @update:modelValue="update(item); toggleActive(index);"
                    >
                      <v-btn size="x-small" variant="elevated" class="error" @click="toggleActive(index)">Cancel</v-btn>
                    </v-date-picker>
                  </div>

                </template>
                <v-divider></v-divider>
              </v-list>
            </v-col>
          </v-row>

          <!-- Add Certifications List -->
          <div v-if="data.openCertificationsForm">
            <v-list-subheader>Check all certifications that apply</v-list-subheader>
            <v-row>
              <v-col>
                <v-checkbox
                    v-for="(item) in data.certificationTypes"
                    :key="item.id"
                    class="ml-4 mt-0"
                    v-model="item.value"
                    :label="item.label"
                ></v-checkbox>

                <v-card-actions>
                  <v-btn
                      class="error"
                      @click="data.openCertificationsForm = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn class="primary" @click="submit"> Submit </v-btn>
                </v-card-actions>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}
</style>